import React, { useEffect, useState, useRef } from 'react';
import { timelogAPI, projectAPI, taskAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { FiPlay, FiSquare, FiClock, FiCalendar, FiTrendingUp, FiTrash2, FiPlus, FiX, FiCoffee } from 'react-icons/fi';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export default function TimeTracking() {
  const { user: _user } = useAuth(); // eslint-disable-line
  const [logs,setLogs]=useState([]); const [projects,setProjects]=useState([]); const [tasks,setTasks]=useState([]);
  const [running,setRunning]=useState(false); const [elapsed,setElapsed]=useState(0); const [startTime,setStartTime]=useState(null);
  const [timerForm,setTimerForm]=useState({project:'',task:'',description:''});
  const [logModal,setLogModal]=useState(false);
  const [manualForm,setManualForm]=useState({project:'',task:'',description:'',hours:'',date:new Date().toISOString().slice(0,10)});
  const [month,setMonth]=useState(new Date().getMonth()+1); const [year,setYear]=useState(new Date().getFullYear());
  const [summary,setSummary]=useState(null);
  const [breakRunning,setBreakRunning]=useState(false); const [breakElapsed,setBreakElapsed]=useState(0); const [breakStart,setBreakStart]=useState(null);
  const timerRef = useRef(null); const breakRef = useRef(null);

  useEffect(()=>{ load(); return ()=>{ clearInterval(timerRef.current); clearInterval(breakRef.current); }; },[month,year]);

  const load = async () => {
    try {
      const [lR,pR,sR] = await Promise.all([
        timelogAPI.getAll({month,year}),
        projectAPI.getAll(),
        timelogAPI.getSummary()
      ]);
      setLogs(lR.data.data||[]); setProjects(pR.data.projects||[]); setSummary(sR.data.data||null);
    } catch(e){ console.error(e); toast.error('Failed to load time logs'); }
  };

  const loadTasks = async (pid) => {
    if(!pid) return setTasks([]);
    try { const r = await taskAPI.getAll({project:pid}); setTasks(r.data.tasks||[]); } catch {}
  };

  const startTimer = () => {
    if(!timerForm.project) return toast.error('Select a project first');
    const now = Date.now(); setStartTime(now); setRunning(true);
    timerRef.current = setInterval(()=>setElapsed(Math.floor((Date.now()-now)/1000)), 1000);
  };

  const startBreak = () => {
    if (breakRunning) return;
    const now = Date.now(); setBreakStart(now); setBreakRunning(true); setBreakElapsed(0);
    breakRef.current = setInterval(()=>setBreakElapsed(Math.floor((Date.now()-now)/1000)), 1000);
    toast('Break started ☕', { icon: '⏸' });
  };

  const stopBreak = () => {
    clearInterval(breakRef.current); setBreakRunning(false);
    const mins = Math.round(breakElapsed/60);
    toast.success(`Break ended — ${mins} min${mins!==1?'s':''}`);
    setBreakElapsed(0); setBreakStart(null);
  };

  const stopTimer = async () => {
    clearInterval(timerRef.current); setRunning(false);
    if (breakRunning) { clearInterval(breakRef.current); setBreakRunning(false); setBreakElapsed(0); setBreakStart(null); }
    const hrs = Math.round((elapsed/3600)*100)/100;
    if(hrs < 0.01) return toast.error('Too short to log');
    try {
      await timelogAPI.create({ ...timerForm, hours: hrs, startTime: new Date(startTime), endTime: new Date(), date: new Date(startTime) });
      toast.success(`${hrs}h logged!`); setElapsed(0); setStartTime(null); load();
    } catch { toast.error('Failed to save'); }
  };

  const addManual = async () => {
    if(!manualForm.project) return toast.error('Please select a project');
    if(!manualForm.hours) return toast.error('Hours are required');
    if(parseFloat(manualForm.hours) <= 0) return toast.error('Hours must be greater than 0');
    if(parseFloat(manualForm.hours) > 24) return toast.error('Hours cannot exceed 24 in a single entry');
    try { await timelogAPI.create({...manualForm,hours:parseFloat(manualForm.hours)}); toast.success('Time logged!'); setLogModal(false); load(); }
    catch(e) { toast.error(e.response?.data?.message||'Failed to log time'); }
  };

  const deleteLog = async (id) => {
    try { await timelogAPI.delete(id); toast.success('Deleted'); load(); } catch { toast.error('Failed'); }
  };

  const fmt = s=>`${String(Math.floor(s/3600)).padStart(2,'0')}:${String(Math.floor((s%3600)/60)).padStart(2,'0')}:${String(s%60).padStart(2,'0')}`;
  const totalHrs = logs.reduce((s,l)=>s+(l.duration||0)/60,0);
  const billableHrs = logs.filter(l=>l.isBillable).reduce((s,l)=>s+(l.duration||0)/60,0);

  const S={
    page:{maxWidth:1200,margin:'0 auto',fontFamily:"'Sora',sans-serif"},
    card:{background:'#fff',borderRadius:16,padding:24,boxShadow:'0 2px 12px #0000000a',border:'1px solid #f1f5f9',marginBottom:20},
    btn:(v,sm)=>({padding:sm?'7px 14px':'10px 20px',borderRadius:10,border:'none',fontWeight:600,fontSize:sm?12:14,cursor:'pointer',display:'inline-flex',alignItems:'center',gap:6,transition:'all .2s',
      ...(v==='primary'?{background:'var(--pm-gradient)',color:'#fff'}:v==='success'?{background:'#22c55e',color:'#fff'}:v==='danger'?{background:'#ef4444',color:'#fff'}:{background:'#f8fafc',color:'#64748b',border:'1.5px solid #e2e8f0'})}),
    inp:{width:'100%',padding:'9px 13px',border:'2px solid #e2e8f0',borderRadius:9,fontSize:14,outline:'none',boxSizing:'border-box',marginTop:4},
    overlay:{position:'fixed',inset:0,background:'#00000060',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:20},
    modal:{background:'#fff',borderRadius:20,padding:32,width:'100%',maxWidth:480,maxHeight:'90vh',overflowY:'auto'},
    label:{display:'block',fontSize:13,fontWeight:600,color:'#475569',marginBottom:12},
  };

  return (
    <div style={S.page}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&display=swap')`}</style>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:28}}>
        <div><h1 style={{margin:0,fontSize:28,fontWeight:800,color:'#0f172a'}}>⏱ Time Tracking</h1>
          <p style={{margin:'4px 0 0',color:'#64748b'}}>Track billable hours on projects and tasks</p></div>
        <button style={S.btn('ghost')} onClick={()=>setLogModal(true)}><FiPlus/> Log Manual Time</button>
      </div>

      {/* KPI cards */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))',gap:16,marginBottom:24}}>
        {[
          {i:<FiClock/>,l:'This Period',v:`${totalHrs.toFixed(1)}h`,c:'var(--pm-primary)'},
          {i:<FiTrendingUp/>,l:'Billable',v:`${billableHrs.toFixed(1)}h`,c:'#22c55e'},
          {i:<FiCalendar/>,l:'Log Entries',v:logs.length,c:'#f59e0b'},
          {i:<FiClock/>,l:'All-time Total',v:`${(summary?.totalHours||0).toFixed(0)}h`,c:'#0ea5e9'},
        ].map(s=>(
          <div key={s.l} style={{...S.card,marginBottom:0,borderLeft:`4px solid ${s.c}`}}>
            <div style={{color:s.c,fontSize:22,marginBottom:8}}>{s.i}</div>
            <div style={{fontSize:26,fontWeight:800,color:'#0f172a'}}>{s.v}</div>
            <div style={{fontSize:12,color:'#64748b',marginTop:2}}>{s.l}</div>
          </div>
        ))}
      </div>

      {/* Live Timer */}
      <div style={{...S.card,background:'linear-gradient(135deg,#0f172a,#1e293b)',border:'none',marginBottom:24}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
          <h3 style={{margin:0,color:'#f8fafc',fontSize:16,fontWeight:700}}>⚡ Live Timer</h3>
          <div style={{fontSize:52,fontWeight:800,color:running?'#22c55e':'#475569',fontVariantNumeric:'tabular-nums',letterSpacing:2}}>{fmt(elapsed)}</div>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))',gap:12,marginBottom:16}}>
          <select style={{...S.inp,background:'#1e293b',border:'2px solid #334155',color:'#f8fafc'}}
            value={timerForm.project} onChange={e=>{setTimerForm({...timerForm,project:e.target.value,task:''});loadTasks(e.target.value);}}>
            <option value="">Select Project *</option>
            {projects.map(p=><option key={p._id} value={p._id}>{p.name}</option>)}
          </select>
          <select style={{...S.inp,background:'#1e293b',border:'2px solid #334155',color:'#f8fafc'}}
            value={timerForm.task} onChange={e=>setTimerForm({...timerForm,task:e.target.value})}>
            <option value="">Select Task</option>
            {tasks.map(t=><option key={t._id} value={t._id}>{t.title}</option>)}
          </select>
          <input style={{...S.inp,background:'#1e293b',border:'2px solid #334155',color:'#f8fafc'}}
            value={timerForm.description} onChange={e=>setTimerForm({...timerForm,description:e.target.value})} placeholder="What are you working on?"/>
        </div>
        <div style={{display:'flex',gap:12,alignItems:'center',flexWrap:'wrap'}}>
          {!running
            ? <button style={{...S.btn('success'),fontSize:15,padding:'12px 28px'}} onClick={startTimer}><FiPlay/> Start Timer</button>
            : <button style={{...S.btn('danger'),fontSize:15,padding:'12px 28px'}} onClick={stopTimer}><FiSquare/> Stop & Save</button>
          }
          {!breakRunning
            ? <button style={{padding:'12px 20px',borderRadius:10,border:'2px solid #f59e0b',background:'transparent',color:'#f59e0b',fontWeight:600,fontSize:14,cursor:'pointer',display:'inline-flex',alignItems:'center',gap:6}} onClick={startBreak}><FiCoffee/> Start Break</button>
            : <button style={{padding:'12px 20px',borderRadius:10,border:'none',background:'#f59e0b',color:'#fff',fontWeight:600,fontSize:14,cursor:'pointer',display:'inline-flex',alignItems:'center',gap:8}} onClick={stopBreak}>
                <FiCoffee/>
                <span>Break: {fmt(breakElapsed)}</span>
                <FiSquare size={14}/>
              </button>
          }
        </div>
        {breakRunning && (
          <div style={{marginTop:14,padding:'10px 16px',background:'rgba(245,158,11,0.15)',borderRadius:10,border:'1px solid rgba(245,158,11,0.3)',display:'flex',alignItems:'center',gap:10}}>
            <FiCoffee style={{color:'#f59e0b',flexShrink:0}}/>
            <div>
              <div style={{color:'#f59e0b',fontWeight:700,fontSize:13}}>Break in progress</div>
              <div style={{color:'#fbbf24',fontSize:22,fontWeight:800,fontVariantNumeric:'tabular-nums',letterSpacing:1}}>{fmt(breakElapsed)}</div>
            </div>
          </div>
        )}
      </div>

      {/* Month filter */}
      <div style={{display:'flex',gap:10,marginBottom:16,alignItems:'center'}}>
        <select value={month} onChange={e=>setMonth(+e.target.value)} style={{padding:'8px 14px',borderRadius:8,border:'1.5px solid #e2e8f0',fontSize:14,cursor:'pointer'}}>
          {MONTHS.map((m,i)=><option key={m} value={i+1}>{m}</option>)}
        </select>
        <select value={year} onChange={e=>setYear(+e.target.value)} style={{padding:'8px 14px',borderRadius:8,border:'1.5px solid #e2e8f0',fontSize:14,cursor:'pointer'}}>
          {[2024,2025,2026].map(y=><option key={y} value={y}>{y}</option>)}
        </select>
        <span style={{fontSize:13,color:'#64748b',fontWeight:600}}>Showing {MONTHS[month-1]} {year}</span>
      </div>

      {/* Logs table */}
      <div style={S.card}>
        <h3 style={{margin:'0 0 16px',fontSize:16,fontWeight:700}}>Time Logs</h3>
        <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
            <thead>
              <tr style={{background:'#f8fafc'}}>
                {['Date','Project','Task','Description','Duration','Billable',''].map(h=>(
                  <th key={h} style={{padding:'10px 14px',textAlign:'left',fontWeight:700,color:'#64748b',borderBottom:'2px solid #f1f5f9'}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {logs.map(l=>(
                <tr key={l._id} style={{borderBottom:'1px solid #f8fafc'}} onMouseEnter={e=>e.currentTarget.style.background='#fafbff'} onMouseLeave={e=>e.currentTarget.style.background='#fff'}>
                  <td style={{padding:'10px 14px',color:'#64748b'}}>{new Date(l.startTime||l.createdAt).toLocaleDateString()}</td>
                  <td style={{padding:'10px 14px',fontWeight:600,color:'#0f172a'}}>{l.project?.name||'—'}</td>
                  <td style={{padding:'10px 14px',color:'#64748b'}}>{l.task?.title||'—'}</td>
                  <td style={{padding:'10px 14px',color:'#475569',maxWidth:200,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{l.description||'—'}</td>
                  <td style={{padding:'10px 14px',fontWeight:700,color:'var(--pm-primary)'}}>{l.duration?`${(l.duration/60).toFixed(1)}h`:'—'}</td>
                  <td style={{padding:'10px 14px'}}>{l.isBillable?<span style={{color:'#22c55e',fontWeight:700}}>✓</span>:<span style={{color:'#94a3b8'}}>—</span>}</td>
                  <td style={{padding:'10px 14px'}}><button onClick={()=>deleteLog(l._id)} style={{background:'none',border:'none',cursor:'pointer',color:'#94a3b8',padding:4}}><FiTrash2/></button></td>
                </tr>
              ))}
              {logs.length===0&&<tr><td colSpan={7} style={{textAlign:'center',padding:40,color:'#94a3b8'}}>No time logs for this period</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Manual log modal */}
      {logModal&&(
        <div style={S.overlay} onClick={e=>e.target===e.currentTarget&&setLogModal(false)}>
          <div style={S.modal}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:22}}>
              <h3 style={{margin:0,fontSize:18,fontWeight:700}}>Log Manual Time</h3>
              <button style={{background:'none',border:'none',cursor:'pointer',fontSize:22}} onClick={()=>setLogModal(false)}><FiX/></button>
            </div>
            <label style={S.label}>Date<input type="date" style={S.inp} value={manualForm.date} onChange={e=>setManualForm({...manualForm,date:e.target.value})}/></label>
            <label style={S.label}>Project *
              <select style={S.inp} value={manualForm.project} onChange={e=>{setManualForm({...manualForm,project:e.target.value,task:''});loadTasks(e.target.value);}}>
                <option value="">Select project</option>
                {projects.map(p=><option key={p._id} value={p._id}>{p.name}</option>)}
              </select>
            </label>
            <label style={S.label}>Task
              <select style={S.inp} value={manualForm.task} onChange={e=>setManualForm({...manualForm,task:e.target.value})}>
                <option value="">Select task</option>
                {tasks.map(t=><option key={t._id} value={t._id}>{t.title}</option>)}
              </select>
            </label>
            <label style={S.label}>Description<input style={S.inp} value={manualForm.description} onChange={e=>setManualForm({...manualForm,description:e.target.value})} placeholder="What did you work on?"/></label>
            <label style={S.label}>Hours * <input type="number" style={S.inp} step="0.5" min="0.1" value={manualForm.hours} onChange={e=>setManualForm({...manualForm,hours:e.target.value})} placeholder="e.g. 2.5"/></label>
            <div style={{display:'flex',gap:10,justifyContent:'flex-end',marginTop:12}}>
              <button style={S.btn('ghost')} onClick={()=>setLogModal(false)}>Cancel</button>
              <button style={S.btn('primary')} onClick={addManual}><FiPlus/> Save Log</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
